/**
 * REDESIGN §2 (urgency buckets) + §3 (the written summary "מה קורה"), business side.
 *
 * The rules live ONCE, in `shared/insight` (`docs/insight-contract.md`): this server's whole job is
 * to **supply facts** — an `UrgencyInput[]` and a `BusinessNarrativeInput` — and to pass the result
 * through in `summary.urgency` / `summary.narrative`. Bucketing, sorting, the Hebrew sentences and
 * the countdown chips are never re-implemented here.
 *
 * Every date comes from the payloads the home screen already renders (`payloads.ts`), so a row in
 * `עכשיו` and the card it links to can never disagree; `asOf` is `freshness.asOf`, never `todayIL()`.
 */
import { buildItems, buildNarrative, countByBucket, groupByBucket } from '../../shared/insight/src/index.ts';
import type { BusinessNarrativeInput, UrgencyInput } from '../../shared/insight/src/index.ts';
import type { Agorot, BalancePoint, DayString, ForecastEvent, FreshnessLevel, Narrative, TaxEvent, UrgencySummary, VatPeriodStatus } from '../api-types.ts';
import type { InvoiceView } from './metrics/receivables.ts';
import { fmtDayShort, fmtMoney, providerLabel } from './util/format.ts';
import { daysBetween } from './util/dates.ts';

/**
 * How many invoice rows of each kind reach the home screen. The list itself is `#invoices`; the
 * urgency block names the ones that actually need a call, oldest/nearest first.
 */
export const MAX_INVOICE_ITEMS = 5;
/** An invoice further out than this is not "coming up" yet — it would only pad `אפשר לחכות`. */
export const INVOICE_DUE_HORIZON_DAYS = 30;

/**
 * Exactly the slice of `DataContext` this module reads. Narrow on purpose: the whole file stays a
 * pure function of facts (no DB, no clock), so the tests build one of these by hand — and a real
 * `DataContext` is structurally assignable to it.
 */
export interface InsightContext {
  asOf: DayString;
  invoices: readonly InvoiceView[];
  accounts: ReadonlyArray<{ id: string; connectionId: string; assigned: boolean }>;
  connections: ReadonlyArray<{ id: string; provider_id: string; status: string; expiry_date: string | null }>;
  freshness: { level: FreshnessLevel };
}

export interface InsightInput {
  data: InsightContext;
  /** A2 · the three NET tiles. */
  month: { incomeNetAgorot: Agorot; expenseNetAgorot: Agorot; profitAgorot: Agorot };
  /** B1 · the collection card. */
  collection: { openAgorot: Agorot; overdueAgorot: Agorot; topDebtors: ReadonlyArray<{ customerId: string; customerName: string; overdueAgorot: Agorot }> };
  /** B2 · the VAT card. */
  tax: { period: { id: string; label: string; status: VatPeriodStatus } | null; dueDate: DayString | null; netVatAgorot: Agorot };
  /** The full deadline calendar (`buildTaxEventList`), not only the non-VAT rows of `tax.upcoming`. */
  deadlines: readonly TaxEvent[];
  /** The business card cycle. */
  card: { account: { id: string; name: string; providerId: string }; next: { dueDate: DayString; status: string; amountAgorot: Agorot } | null } | null;
  forecast: { computable: boolean; floorAgorot: Agorot; series: readonly BalancePoint[]; events: readonly ForecastEvent[]; horizons: ReadonlyArray<{ balanceAgorot: Agorot }> };
  /** Σ gross and count of the period's expenses with no tax invoice (spec §4.5). */
  missingInvoices: { count: number; grossAgorot: number; vatAgorot: number };
  /** The last `sync_runs` row — `ok === 0` is REDESIGN §2's "anything failed". */
  lastSync: { ok: number | null; error: string | null } | null;
}

// ───────────────────────────── §2 · the urgency feed ─────────────────────────────

/** Connection states that need the owner to re-link in the Financy app (docs §4). */
const NEEDS_RELINK: ReadonlySet<string> = new Set(['EXPIRED', 'REVOKED', 'REJECTED']);

/** Unpaid, not credited — the two invoice states that can appear on the home screen. */
function openInvoices(invoices: readonly InvoiceView[]): InvoiceView[] {
  return invoices.filter((inv) => inv.status === 'open' || inv.status === 'overdue');
}

/**
 * Every source REDESIGN §2 lists for the business system, in the shapes `docs/insight-contract.md`
 * §1.3 fixes:
 *
 * `vat_report` / `vat_payment` (the period's own deadline, whichever step is still open) ·
 * `tax_advance` / `national_insurance` (the advance calendar) · `invoice_overdue` / `invoice_due` ·
 * `supplier_standing_order` (the forecast's standing-order events) · `card_charge` (the closed cycle
 * waiting to be charged) · `expense_no_invoice` (undated → `אפשר לחכות`) · `forecast_negative` ·
 * `connection_expired` / `data_stale` / `refresh_failed`.
 */
export function buildUrgencyInputs(input: InsightInput): UrgencyInput[] {
  const { data } = input;
  const out: UrgencyInput[] = [];

  // ── VAT: the report and the payment are two steps behind ONE deadline. Emitting both would print
  //    the same date twice, so the period's status picks the one that is still open.
  const period = input.tax.period;
  if (period !== null && input.tax.dueDate !== null && period.status !== 'paid') {
    const reporting = period.status === 'open';
    out.push({
      id: `vat:${period.id}`,
      kind: reporting ? 'vat_report' : 'vat_payment',
      date: input.tax.dueDate,
      title: `${reporting ? 'דיווח' : 'תשלום'} מע"מ ל${period.label}`,
      amountAgorot: -Math.max(0, input.tax.netVatAgorot),
      actionHref: `#vat?period=${period.id}`,
    });
  }

  // ── income-tax advances and National Insurance (VAT rows are already covered above).
  for (const event of input.deadlines) {
    if (event.type === 'vat' || event.paidAt !== null) continue;
    out.push({
      id: `tax:${event.id}`,
      kind: event.type === 'income_tax_advance' ? 'tax_advance' : 'national_insurance',
      date: event.dueDate,
      title: event.label,
      amountAgorot: -event.amountAgorot,
      actionHref: '#vat',
    });
  }

  // ── receivables. Overdue first (they are late by construction), then what falls due this month.
  const open = openInvoices(data.invoices);
  const overdue = open
    .filter((inv) => inv.dueDate < data.asOf)
    .sort((a, b) => (a.dueDate === b.dueDate ? b.grossAgorot - a.grossAgorot : a.dueDate < b.dueDate ? -1 : 1))
    .slice(0, MAX_INVOICE_ITEMS);
  for (const inv of overdue) {
    out.push({
      id: `invoice:${inv.number}`,
      kind: 'invoice_overdue',
      date: inv.dueDate,
      title: `חשבונית ${inv.number} · ${inv.customerName}`,
      amountAgorot: inv.grossAgorot,
      actionHref: `#invoices?inv=${encodeURIComponent(inv.number)}`,
    });
  }
  const due = open
    .filter((inv) => inv.dueDate >= data.asOf && daysBetween(data.asOf, inv.dueDate) <= INVOICE_DUE_HORIZON_DAYS)
    .sort((a, b) => (a.dueDate === b.dueDate ? b.grossAgorot - a.grossAgorot : a.dueDate < b.dueDate ? -1 : 1))
    .slice(0, MAX_INVOICE_ITEMS);
  for (const inv of due) {
    out.push({
      id: `invoice:${inv.number}`,
      kind: 'invoice_due',
      date: inv.dueDate,
      title: `חשבונית ${inv.number} · ${inv.customerName}`,
      amountAgorot: inv.grossAgorot,
      actionHref: `#invoices?inv=${encodeURIComponent(inv.number)}`,
    });
  }

  // ── the card charge: the closed cycle the bank has not taken yet (spec §2.2).
  const cycle = input.card?.next ?? null;
  if (input.card !== null && cycle !== null && cycle.status !== 'charged' && cycle.amountAgorot > 0) {
    out.push({
      id: `card:${input.card.account.id}:${cycle.dueDate}`,
      kind: 'card_charge',
      date: cycle.dueDate,
      title: `חיוב כרטיס ${input.card.account.name}`,
      amountAgorot: -cycle.amountAgorot,
      providerId: input.card.account.providerId,
      actionHref: '#cashflow',
    });
  }

  // ── supplier standing orders leaving the bank inside the forecast window.
  for (const event of input.forecast.events) {
    if (event.kind !== 'standing_order' && event.kind !== 'supplier') continue;
    if (event.amountAgorot >= 0) continue;
    out.push({
      id: `so:${event.kind}:${event.day}:${event.label}`,
      kind: 'supplier_standing_order',
      date: event.day,
      title: event.label,
      amountAgorot: event.amountAgorot,
      actionHref: '#cashflow',
    });
  }

  // ── the forecast crossing its floor (spec §4.2 `FORECAST_NEGATIVE`).
  const crossing = input.forecast.computable ? input.forecast.series.find((p) => !p.actual && p.balanceAgorot < input.forecast.floorAgorot) : undefined;
  if (crossing) {
    out.push({
      id: `forecast:${crossing.day}`,
      kind: 'forecast_negative',
      date: crossing.day,
      title: `לפי הצפי היתרה יורדת ל-${fmtMoney(crossing.balanceAgorot)} ב-${fmtDayShort(crossing.day)}`,
      amountAgorot: crossing.balanceAgorot,
      actionHref: '#cashflow',
    });
  }

  // ── expenses with no tax invoice: a trend, never an event → `אפשר לחכות` (the registry pins it).
  if (input.missingInvoices.count > 0) {
    out.push({
      id: 'expense_no_invoice',
      kind: 'expense_no_invoice',
      date: null,
      title: `${input.missingInvoices.count} הוצאות ללא חשבונית מס`,
      amountAgorot: -input.missingInvoices.grossAgorot,
      actionHref: '#expenses?noinvoice=1',
    });
  }

  // ── connection state. §8: an expired consent whose data is still fresh stays in `אפשר לחכות`; one
  //    that is actually blocking the refresh is pinned to `עכשיו`.
  const blocking = data.freshness.level === 'stale_warn' || data.freshness.level === 'stale_critical' || data.freshness.level === 'none';
  for (const conn of data.connections) {
    if (!NEEDS_RELINK.has(conn.status)) continue;
    if (!data.accounts.some((a) => a.connectionId === conn.id && a.assigned)) continue;
    out.push({
      id: `conn:${conn.id}`,
      kind: 'connection_expired',
      date: conn.expiry_date,
      title: `החיבור ל-${providerLabel(conn.provider_id)} פג — צריך לחדש`,
      amountAgorot: null,
      providerId: conn.provider_id,
      actionHref: '#settings?tab=connections',
      ...(blocking ? { pin: 'now' as const } : {}),
    });
  }

  if (data.freshness.level === 'stale_warn' || data.freshness.level === 'stale_critical') {
    out.push({
      id: 'data_stale',
      kind: 'data_stale',
      date: null,
      title: 'הנתונים לא התרעננו',
      amountAgorot: null,
      severity: data.freshness.level === 'stale_critical' ? 'critical' : 'warn',
      actionHref: '#settings?tab=connections',
    });
  }

  if (input.lastSync !== null && input.lastSync.ok === 0) {
    out.push({
      id: 'refresh_failed',
      kind: 'refresh_failed',
      date: null,
      title: 'הסנכרון האחרון נכשל',
      amountAgorot: null,
      failed: true,
      actionHref: '#settings?tab=connections',
    });
  }

  return out;
}

/** `buildItems` + `groupByBucket` + `countByBucket` — the three shapes the contract delivers. */
export function buildUrgency(input: InsightInput): UrgencySummary {
  const items = buildItems(buildUrgencyInputs(input), input.data.asOf);
  return { items, groups: groupByBucket(items), counts: countByBucket(items) };
}

// ───────────────────────────── §3 · the narrative ─────────────────────────────

/**
 * The facts REDESIGN §3's business templates need, from the payloads the home screen already has
 * (`docs/insight-contract.md` §2.1). Every field that cannot be computed is `null`, which DROPS its
 * sentence — the package never hedges and never prints a placeholder.
 */
export function buildNarrativeInput(input: InsightInput): BusinessNarrativeInput {
  const { data } = input;
  const empty = data.accounts.length === 0 && data.invoices.length === 0;

  const top = input.collection.topDebtors[0] ?? null;
  const topOverdueDays = top === null ? null : maxOverdueDays(data.invoices, top.customerId);
  const collection =
    top !== null && top.overdueAgorot > 0 && topOverdueDays !== null
      ? {
          openAgorot: input.collection.openAgorot,
          overdueAgorot: input.collection.overdueAgorot,
          topDebtor: { name: top.customerName, amountAgorot: top.overdueAgorot, days: topOverdueDays },
        }
      : input.collection.openAgorot > 0
        ? { openAgorot: input.collection.openAgorot, overdueAgorot: input.collection.overdueAgorot, topDebtor: null }
        : null;

  const period = input.tax.period;
  return {
    system: 'business',
    month: empty ? null : { inAgorot: input.month.incomeNetAgorot, outAgorot: input.month.expenseNetAgorot, profitAgorot: input.month.profitAgorot },
    collection,
    vat: period === null || input.tax.dueDate === null ? null : { periodLabel: period.label, dueDate: input.tax.dueDate, amountAgorot: input.tax.netVatAgorot },
    runway: input.forecast.computable ? { forecastAgorot: input.forecast.horizons[0]?.balanceAgorot ?? 0 } : null,
    missingInvoices: input.missingInvoices.count === 0 ? null : { count: input.missingInvoices.count, amountAgorot: input.missingInvoices.grossAgorot, vatAgorot: input.missingInvoices.vatAgorot },
  };
}

/** The age of that customer's oldest unpaid overdue invoice — the `{days}` of `b.collection`. */
function maxOverdueDays(invoices: readonly InvoiceView[], customerId: string): number | null {
  let days: number | null = null;
  for (const inv of invoices) {
    if (inv.customerId !== customerId || inv.status !== 'overdue' || inv.overdueDays === null) continue;
    if (days === null || inv.overdueDays > days) days = inv.overdueDays;
  }
  return days;
}

export function buildBusinessNarrative(input: InsightInput): Narrative {
  return buildNarrative(buildNarrativeInput(input), input.data.asOf);
}
